import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ShieldCheck, Gem, Truck, ArrowRight } from "lucide-react";

const SPARKLES = [
    { top: "18%", left: "12%", delay: "0s" },
    { top: "30%", left: "78%", delay: "0.8s" },
    { top: "62%", left: "20%", delay: "1.6s" },
    { top: "50%", left: "88%", delay: "2.2s" },
    { top: "75%", left: "55%", delay: "1.2s" },
];

export default function HomePage() {
    const [featured, setFeatured] = useState([]);
    const [products, setProducts] = useState([]);

    useEffect(() => {
        api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {});
        api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    }, []);

    const hero = featured[0];
    const chains = products.filter((p) => p.category === "chaines");
    const earrings = products.filter((p) => p.category === "boucles");

    return (
        <div className="relative">
            {/* HERO */}
            <section className="relative grain overflow-hidden">
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(circle at 50% 15%, rgba(30,58,138,0.28) 0%, rgba(10,10,12,0.95) 65%)",
                    }}
                />
                {SPARKLES.map((s, i) => (
                    <div key={i} className="sparkle" style={{ ...s, animationDelay: s.delay }} />
                ))}
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-20 pb-16 sm:pt-28 sm:pb-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div className="fade-in-up">
                        <div className="text-[10px] uppercase tracking-[0.35em] text-slate-400 mb-6 font-mono-tight">
                            Argent 925 · Moissanite certifiée GRA
                        </div>
                        <h1 data-testid="hero-title" className="font-serif-luxe text-4xl sm:text-6xl lg:text-7xl leading-[0.95] chrome-text font-semibold">
                            L'éclat diamant.<br />Sans compromis.
                        </h1>
                        <p className="mt-6 text-slate-400 max-w-md leading-relaxed">
                            Chaînes cubaines et boucles d'oreilles en argent 925 serti de moissanite VVS1,
                            certifiées GRA. Conçues pour le streetwear premium, faites pour durer.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to="/boutique/chaines" data-testid="hero-cta-chaines" className="btn-primary">
                                Explorer les chaînes <ArrowRight className="w-4 h-4 ml-2" />
                            </Link>
                            <Link to="/authenticite" data-testid="hero-cta-gra" className="btn-ghost">
                                Certificat GRA
                            </Link>
                        </div>
                        <div className="mt-10 flex items-center gap-8 text-[11px] uppercase tracking-[0.2em] text-slate-500">
                            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> GRA</div>
                            <div className="flex items-center gap-2"><Gem className="w-4 h-4" /> VVS1</div>
                            <div className="flex items-center gap-2"><Truck className="w-4 h-4" /> Livraison gratuite dès 500 $</div>
                        </div>
                    </div>
                    {hero && (
                        <Link to={`/produit/${hero.slug}`} data-testid="hero-featured-product" className="relative">
                            <div className="zoom-wrap rounded-3xl overflow-hidden border border-white/10 shadow-[0_40px_120px_-40px_rgba(30,58,138,0.6)]">
                                <img
                                    src={fileUrl(hero.images?.[0])}
                                    alt={hero.name}
                                    className="w-full aspect-[4/5] object-cover"
                                />
                                <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/85 to-transparent">
                                    <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400">Pièce signature</div>
                                    <div className="font-serif-luxe text-2xl text-slate-100 mt-1">{hero.name}</div>
                                </div>
                            </div>
                        </Link>
                    )}
                </div>
            </section>

            {/* COLLECTIONS BENTO */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.35em] text-slate-500 mb-3">Collections</div>
                        <h2 className="font-serif-luxe text-3xl sm:text-4xl text-slate-100">Deux silhouettes.<br />Un même éclat.</h2>
                    </div>
                    <Link to="/boutique" data-testid="collections-view-all" className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-300 hover:text-white transition-colors">
                        Tout voir <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                    <Link
                        to="/boutique/chaines"
                        data-testid="collection-card-chaines"
                        className="md:col-span-8 relative rounded-3xl overflow-hidden group min-h-[340px] glass-card"
                    >
                        <img
                            src={chains[0] ? fileUrl(chains[0].images?.[0]) : ""}
                            alt="Chaînes cubaines"
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                        <div className="absolute bottom-0 p-8">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Collection</div>
                            <div className="font-serif-luxe text-3xl text-slate-100">Chaînes</div>
                            <div className="mt-2 text-sm text-slate-300 max-w-md">
                                Cubaines et Tennis — maillons sculptés main, sertissage moissanite pierre par pierre.
                            </div>
                        </div>
                    </Link>
                    <Link
                        to="/boutique/boucles"
                        data-testid="collection-card-boucles"
                        className="md:col-span-4 relative rounded-3xl overflow-hidden group min-h-[340px] glass-card"
                    >
                        <img
                            src={earrings[0] ? fileUrl(earrings[0].images?.[0]) : ""}
                            alt="Boucles d'oreilles"
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                        <div className="absolute bottom-0 p-8">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Collection</div>
                            <div className="font-serif-luxe text-3xl text-slate-100">Boucles d'oreilles</div>
                            <div className="mt-2 text-sm text-slate-300">Studs moissanite, coupe brillant ou princesse.</div>
                        </div>
                    </Link>
                    <Link
                        to="/authenticite"
                        data-testid="collection-card-gra"
                        className="md:col-span-12 relative rounded-3xl overflow-hidden group glass-card p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
                    >
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D97706] mb-2">Preuve d'authenticité</div>
                            <div className="font-serif-luxe text-2xl sm:text-3xl text-slate-100">
                                Chaque pierre est certifiée GRA.
                            </div>
                            <p className="mt-3 text-sm text-slate-400 max-w-xl">
                                Gemological Research Association — certificat individuel, numéro de série unique,
                                vérifiable en ligne. Fourni avec chaque commande.
                            </p>
                        </div>
                        <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-slate-200">
                            Découvrir <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                    </Link>
                </div>
            </section>

            {/* FEATURED */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
                <div className="text-[10px] uppercase tracking-[0.35em] text-slate-500 mb-3">Sélection</div>
                <h2 className="font-serif-luxe text-3xl sm:text-4xl text-slate-100 mb-10">Pièces en avant</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {(featured.length ? featured : products).slice(0, 6).map((p) => (
                        <ProductCard key={p.id} product={p} />
                    ))}
                </div>
            </section>

            {/* BRAND */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                    <div className="md:col-span-5 relative">
                        <div className="rounded-3xl overflow-hidden border border-white/10">
                            <img
                                src={fileUrl("vvs-heritage/products/60d0be70-9cf0-4ee8-8e80-4f5363c1d331.jpeg")}
                                alt="Chaîne moissanite VVS Héritage portée"
                                data-testid="home-brand-photo"
                                className="w-full aspect-[4/5] object-cover"
                                loading="lazy"
                            />
                        </div>
                        <div className="sparkle" style={{ top: "12%", left: "8%", animationDelay: "0.6s" }} />
                        <div className="sparkle" style={{ top: "70%", left: "85%", animationDelay: "1.8s" }} />
                    </div>
                    <div className="md:col-span-7 md:pl-8">
                        <div className="text-[10px] uppercase tracking-[0.35em] text-slate-500 mb-3">La maison</div>
                        <h2 className="font-serif-luxe text-3xl sm:text-5xl chrome-text font-semibold leading-tight">
                            Pensée à Québec.<br />Portée partout.
                        </h2>
                        <p className="mt-6 text-slate-400 leading-relaxed max-w-xl">
                            Chaque pièce VVS Héritage est sertie à la main, contrôlée une par une, puis expédiée
                            avec son certificat GRA unique. On ne vend pas du diamant — on vend l'éclat,
                            la légitimité et la durabilité, au prix qui a du bon sens.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <Link to="/a-propos" data-testid="brand-cta-apropos" className="btn-ghost">Notre histoire</Link>
                            <Link to="/boutique" data-testid="brand-cta-boutique" className="btn-primary">Voir la collection</Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* REASSURANCE */}
            <section className="border-y border-white/10 bg-[#121216]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                    {[
                        { icon: Gem, title: "Moissanite VVS1", sub: "Certifiée GRA" },
                        { icon: ShieldCheck, title: "Argent 925", sub: "Plaqué or 14kt · Anti-ternissement" },
                        { icon: Truck, title: "Postes Canada", sub: "Gratuite dès 500 $" },
                        { icon: ShieldCheck, title: "14 jours", sub: "Retours simples" },
                    ].map((r, i) => (
                        <div key={i} data-testid={`reassurance-${i}`}>
                            <r.icon className="w-6 h-6 mx-auto text-slate-300 mb-3" />
                            <div className="text-sm font-medium text-slate-100">{r.title}</div>
                            <div className="text-xs text-slate-500 mt-1">{r.sub}</div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
